import React, { useCallback, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import chroma from "chroma-js";
import { darkOrLightColor } from "../../helpers/darkOrLightColor";
import {
  wrapper,
  tool_heading,
  subtitle,
  trust_signals,
  editor_grid,
  preview_panel,
  panel_heading,
  gradient_result,
  gradient_value,
  preview_actions,
  primary_button,
  action_button,
  settings,
  gradient_type_selector,
  gradient_type_button,
  gradient_type_button_active,
  field_group,
  orientation_select,
  repeat_control,
  stops_header,
  colors_controls,
  color_inputs,
  color_picker,
  hex_input,
  stop_controls,
  stop_number,
  delete_button,
  add_button,
  presets_container,
  presets_grid,
  preset_button,
  scale_panel,
  colors_scale,
  scale_color,
  toast,
} from "./styles.module.css";

const TEXTS = {
  es: {
    mainTitle: "Generador de degradados CSS",
    subtitle:
      "Crea gradientes lineales, radiales o cónicos, ajusta cada color y copia el CSS listo para usar.",
    instant: "Vista previa instantánea",
    noAccount: "Sin registro",
    free: "Gratis",
    settings: "Editar degradado",
    generatedGradient: "Vista previa del degradado",
    colorRange: "Colores intermedios",
    addColor: "Añadir color",
    orientation: "Dirección y forma",
    gradientType: "Tipo de degradado",
    linear: "Lineal",
    radial: "Radial",
    conic: "Cónico",
    repeating: "Repetir degradado",
    colorStops: "Colores y posiciones",
    color: "Color",
    position: "Posición",
    removeColor: "Eliminar color",
    presets: "Combinaciones rápidas",
    usePreset: "Usar combinación",
    copyCSS: "Copiar regla CSS",
    copyValue: "Copiar gradiente",
    downloadCSS: "Descargar CSS",
    random: "Crear aleatorio",
    reverse: "Invertir colores",
    copied: "Copiado",
    copyError: "No se pudo copiar.",
    downloaded: "Archivo CSS descargado",
  },
  en: {
    mainTitle: "CSS gradient generator",
    subtitle:
      "Create linear, radial, or conic gradients, adjust every color, and copy production-ready CSS.",
    instant: "Instant preview",
    noAccount: "No sign-up",
    free: "Free",
    settings: "Edit gradient",
    generatedGradient: "Gradient preview",
    colorRange: "Intermediate colors",
    addColor: "Add color",
    orientation: "Direction and shape",
    gradientType: "Gradient type",
    linear: "Linear",
    radial: "Radial",
    conic: "Conic",
    repeating: "Repeat gradient",
    colorStops: "Colors and positions",
    color: "Color",
    position: "Position",
    removeColor: "Remove color",
    presets: "Quick combinations",
    usePreset: "Use combination",
    copyCSS: "Copy CSS rule",
    copyValue: "Copy gradient",
    downloadCSS: "Download CSS",
    random: "Create random",
    reverse: "Reverse colors",
    copied: "Copied",
    copyError: "Could not copy.",
    downloaded: "CSS file downloaded",
  },
  pt: {
    mainTitle: "Gerador de gradientes CSS",
    subtitle:
      "Crie gradientes lineares, radiais ou cônicos, ajuste cada cor e copie o CSS pronto para usar.",
    instant: "Prévia instantânea",
    noAccount: "Sem cadastro",
    free: "Grátis",
    settings: "Editar gradiente",
    generatedGradient: "Prévia do gradiente",
    colorRange: "Cores intermediárias",
    addColor: "Adicionar cor",
    orientation: "Direção e forma",
    gradientType: "Tipo de gradiente",
    linear: "Linear",
    radial: "Radial",
    conic: "Cônico",
    repeating: "Repetir gradiente",
    colorStops: "Cores e posições",
    color: "Cor",
    position: "Posição",
    removeColor: "Remover cor",
    presets: "Combinações rápidas",
    usePreset: "Usar combinação",
    copyCSS: "Copiar regra CSS",
    copyValue: "Copiar gradiente",
    downloadCSS: "Baixar CSS",
    random: "Criar aleatório",
    reverse: "Inverter cores",
    copied: "Copiado",
    copyError: "Não foi possível copiar.",
    downloaded: "Arquivo CSS baixado",
  },
  fr: {
    mainTitle: "Générateur de dégradés CSS",
    subtitle:
      "Créez des dégradés linéaires, radiaux ou coniques, ajustez chaque couleur et copiez le CSS prêt à l’emploi.",
    instant: "Aperçu instantané",
    noAccount: "Sans inscription",
    free: "Gratuit",
    settings: "Modifier le dégradé",
    generatedGradient: "Aperçu du dégradé",
    colorRange: "Couleurs intermédiaires",
    addColor: "Ajouter une couleur",
    orientation: "Direction et forme",
    gradientType: "Type de dégradé",
    linear: "Linéaire",
    radial: "Radial",
    conic: "Conique",
    repeating: "Répéter le dégradé",
    colorStops: "Couleurs et positions",
    color: "Couleur",
    position: "Position",
    removeColor: "Supprimer la couleur",
    presets: "Combinaisons rapides",
    usePreset: "Utiliser la combinaison",
    copyCSS: "Copier la règle CSS",
    copyValue: "Copier le dégradé",
    downloadCSS: "Télécharger le CSS",
    random: "Créer au hasard",
    reverse: "Inverser les couleurs",
    copied: "Copié",
    copyError: "Impossible de copier.",
    downloaded: "Fichier CSS téléchargé",
  },
  de: {
    mainTitle: "CSS-Farbverlauf-Generator",
    subtitle:
      "Erstelle lineare, radiale oder konische Verläufe, passe jede Farbe an und kopiere das fertige CSS.",
    instant: "Sofortige Vorschau",
    noAccount: "Ohne Anmeldung",
    free: "Kostenlos",
    settings: "Farbverlauf bearbeiten",
    generatedGradient: "Farbverlaufs-Vorschau",
    colorRange: "Zwischenfarben",
    addColor: "Farbe hinzufügen",
    orientation: "Richtung und Form",
    gradientType: "Verlaufsart",
    linear: "Linear",
    radial: "Radial",
    conic: "Konisch",
    repeating: "Farbverlauf wiederholen",
    colorStops: "Farben und Positionen",
    color: "Farbe",
    position: "Position",
    removeColor: "Farbe entfernen",
    presets: "Schnelle Kombinationen",
    usePreset: "Kombination verwenden",
    copyCSS: "CSS-Regel kopieren",
    copyValue: "Farbverlauf kopieren",
    downloadCSS: "CSS herunterladen",
    random: "Zufällig erstellen",
    reverse: "Farben umkehren",
    copied: "Kopiert",
    copyError: "Kopieren fehlgeschlagen.",
    downloaded: "CSS-Datei heruntergeladen",
  },
};

const makeStop = (color, stop) => ({
  id: `${Date.now()}-${Math.random()}`,
  color,
  stop,
});

const GRADIENT_PRESETS = [
  ["#667EEA", "#764BA2"],
  ["#F093FB", "#F5576C"],
  ["#4FACFE", "#00F2FE"],
  ["#43E97B", "#38F9D7"],
  ["#FA709A", "#FEE140"],
  ["#A8EDEA", "#FED6E3"],
  ["#FF0844", "#FFB199"],
  ["#30CFD0", "#330867"],
];

const ORIENTATIONS = {
  linear: [
    { value: "to right", label: "→ 90°" },
    { value: "to bottom right", label: "↘ 135°" },
    { value: "to bottom", label: "↓ 180°" },
    { value: "to bottom left", label: "↙ 225°" },
    { value: "to left", label: "← 270°" },
    { value: "to top left", label: "↖ 315°" },
    { value: "to top", label: "↑ 0°" },
    { value: "to top right", label: "↗ 45°" },
    { value: "45deg", label: "45°" },
    { value: "90deg", label: "90°" },
    { value: "135deg", label: "135°" },
    { value: "180deg", label: "180°" },
  ],
  radial: [
    { value: "circle at center", label: "Circle · center" },
    { value: "ellipse at center", label: "Ellipse · center" },
    { value: "circle at top", label: "Circle · top" },
    { value: "circle at bottom", label: "Circle · bottom" },
    { value: "circle at top left", label: "Circle · top left" },
  ],
  conic: [
    { value: "from 0deg at center", label: "0° · center" },
    { value: "from 90deg at center", label: "90° · center" },
    { value: "from 180deg at center", label: "180° · center" },
    { value: "from 0deg at top left", label: "0° · top left" },
    { value: "from 0deg at bottom right", label: "0° · bottom right" },
  ],
};

const GradientGenerator = ({ locale }) => {
  const texts = TEXTS[locale] || TEXTS.es;
  const [gradientType, setGradientType] = useState("linear");
  const [colorStops, setColorStops] = useState([
    makeStop("#667EEA", 0),
    makeStop("#764BA2", 100),
  ]);
  const [orientation, setOrientation] = useState("to right");
  const [isRepeating, setIsRepeating] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const sortedStops = useMemo(
    () => [...colorStops].sort((a, b) => a.stop - b.stop),
    [colorStops]
  );

  const gradientCSS = useMemo(() => {
    const stops = sortedStops
      .map(colorStop => `${colorStop.color} ${colorStop.stop}%`)
      .join(", ");
    const functionName = `${
      isRepeating ? "repeating-" : ""
    }${gradientType}-gradient`;
    return `${functionName}(${orientation}, ${stops})`;
  }, [gradientType, isRepeating, orientation, sortedStops]);

  const cssRule = `.element {\n  background-image: ${gradientCSS};\n}`;
  const colorScale = useMemo(
    () =>
      chroma
        .scale(sortedStops.map(colorStop => colorStop.color))
        .mode("lch")
        .colors(7),
    [sortedStops]
  );

  const displayToast = useCallback(message => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2200);
  }, []);

  const copyText = useCallback(
    async value => {
      try {
        await navigator.clipboard.writeText(value);
        displayToast(texts.copied);
      } catch (error) {
        displayToast(texts.copyError);
      }
    },
    [displayToast, texts.copied, texts.copyError]
  );

  const updateColorStop = (id, changes) => {
    setColorStops(currentStops =>
      currentStops.map(colorStop =>
        colorStop.id === id ? { ...colorStop, ...changes } : colorStop
      )
    );
  };

  const updateHexColor = (id, value) => {
    const normalized = value.startsWith("#") ? value : `#${value}`;
    if (chroma.valid(normalized)) {
      updateColorStop(id, { color: chroma(normalized).hex().toUpperCase() });
    }
  };

  const addColorStop = () => {
    if (colorStops.length >= 6) return;

    let largestGap = -1;
    let insertAt = 50;
    let newColor = chroma.random().hex().toUpperCase();

    for (let index = 0; index < sortedStops.length - 1; index += 1) {
      const gap = sortedStops[index + 1].stop - sortedStops[index].stop;
      if (gap > largestGap) {
        largestGap = gap;
        insertAt = Math.round(
          (sortedStops[index].stop + sortedStops[index + 1].stop) / 2
        );
        newColor = chroma
          .mix(
            sortedStops[index].color,
            sortedStops[index + 1].color,
            0.5,
            "lch"
          )
          .hex()
          .toUpperCase();
      }
    }

    setColorStops(currentStops => [
      ...currentStops,
      makeStop(newColor, insertAt),
    ]);
  };

  const removeColorStop = id => {
    if (colorStops.length > 2) {
      setColorStops(currentStops =>
        currentStops.filter(colorStop => colorStop.id !== id)
      );
    }
  };

  const applyPreset = preset => {
    setColorStops([makeStop(preset[0], 0), makeStop(preset[1], 100)]);
  };

  const generateRandom = () => {
    const numberOfColors = Math.floor(Math.random() * 3) + 2;
    setColorStops(
      Array.from({ length: numberOfColors }, (_, index) =>
        makeStop(
          chroma.random().hex().toUpperCase(),
          Math.round((index / (numberOfColors - 1)) * 100)
        )
      )
    );
  };

  const reverseColors = () => {
    setColorStops(
      sortedStops.map((colorStop, index) => ({
        ...colorStop,
        color: sortedStops[sortedStops.length - 1 - index].color,
      }))
    );
  };

  const downloadCSS = () => {
    saveAs(
      new Blob([cssRule], { type: "text/css;charset=utf-8" }),
      "gradient.css"
    );
    displayToast(texts.downloaded);
  };

  const handleGradientTypeChange = type => {
    setGradientType(type);
    setOrientation(ORIENTATIONS[type][0].value);
  };

  return (
    <section className={wrapper} aria-labelledby="gradient-generator-title">
      <header className={tool_heading}>
        <h1 id="gradient-generator-title">{texts.mainTitle}</h1>
        <p className={subtitle}>{texts.subtitle}</p>
        <ul className={trust_signals} aria-label={texts.instant}>
          <li>{texts.instant}</li>
          <li>{texts.noAccount}</li>
          <li>{texts.free}</li>
        </ul>
      </header>

      <div className={editor_grid}>
        <section
          className={preview_panel}
          aria-labelledby="gradient-preview-title"
        >
          <h2 id="gradient-preview-title" className={panel_heading}>
            {texts.generatedGradient}
          </h2>
          <div
            className={gradient_result}
            style={{ backgroundImage: gradientCSS }}
            role="img"
            aria-label={gradientCSS}
          />
          <code className={gradient_value}>{gradientCSS}</code>
          <div className={preview_actions}>
            <button
              type="button"
              className={primary_button}
              onClick={() => copyText(cssRule)}
            >
              {texts.copyCSS}
            </button>
            <button
              type="button"
              className={action_button}
              onClick={() => copyText(gradientCSS)}
            >
              {texts.copyValue}
            </button>
            <button
              type="button"
              className={action_button}
              onClick={downloadCSS}
            >
              {texts.downloadCSS}
            </button>
            <button
              type="button"
              className={action_button}
              onClick={generateRandom}
            >
              {texts.random}
            </button>
            <button
              type="button"
              className={action_button}
              onClick={reverseColors}
            >
              {texts.reverse}
            </button>
          </div>
        </section>

        <aside className={settings} aria-labelledby="gradient-settings-title">
          <h2 id="gradient-settings-title" className={panel_heading}>
            {texts.settings}
          </h2>

          <div className={field_group}>
            <span>{texts.gradientType}</span>
            <div className={gradient_type_selector}>
              {["linear", "radial", "conic"].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`${gradient_type_button} ${
                    gradientType === type ? gradient_type_button_active : ""
                  }`}
                  onClick={() => handleGradientTypeChange(type)}
                  aria-pressed={gradientType === type}
                >
                  {texts[type]}
                </button>
              ))}
            </div>
          </div>

          <label className={field_group}>
            <span>{texts.orientation}</span>
            <select
              className={orientation_select}
              value={orientation}
              onChange={event => setOrientation(event.target.value)}
            >
              {ORIENTATIONS[gradientType].map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className={repeat_control}>
            <input
              type="checkbox"
              checked={isRepeating}
              onChange={event => setIsRepeating(event.target.checked)}
            />
            <span>{texts.repeating}</span>
          </label>

          <div className={stops_header}>
            <h3>{texts.colorStops}</h3>
            <span>{colorStops.length}/6</span>
          </div>

          {colorStops.map((colorStop, index) => (
            <div className={colors_controls} key={colorStop.id}>
              <div className={color_inputs}>
                <input
                  className={color_picker}
                  type="color"
                  value={colorStop.color}
                  onChange={event =>
                    updateColorStop(colorStop.id, {
                      color: event.target.value.toUpperCase(),
                    })
                  }
                  aria-label={`${texts.color} ${index + 1}`}
                />
                <input
                  key={`${colorStop.id}-${colorStop.color}`}
                  className={hex_input}
                  type="text"
                  defaultValue={colorStop.color}
                  onBlur={event =>
                    updateHexColor(colorStop.id, event.target.value)
                  }
                  aria-label={`${texts.color} HEX ${index + 1}`}
                  maxLength={7}
                />
              </div>
              <div className={stop_controls}>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={colorStop.stop}
                  onChange={event =>
                    updateColorStop(colorStop.id, {
                      stop: Number(event.target.value),
                    })
                  }
                  aria-label={`${texts.position} ${index + 1}`}
                />
                <input
                  className={stop_number}
                  type="number"
                  min="0"
                  max="100"
                  value={colorStop.stop}
                  onChange={event =>
                    updateColorStop(colorStop.id, {
                      stop: Math.min(
                        100,
                        Math.max(0, Number(event.target.value))
                      ),
                    })
                  }
                  aria-label={`${texts.position} ${index + 1}`}
                />
                <span>%</span>
                <button
                  type="button"
                  className={delete_button}
                  onClick={() => removeColorStop(colorStop.id)}
                  disabled={colorStops.length <= 2}
                  aria-label={`${texts.removeColor} ${index + 1}`}
                >
                  ×
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            className={add_button}
            onClick={addColorStop}
            disabled={colorStops.length >= 6}
          >
            + {texts.addColor}
          </button>

          <div className={presets_container}>
            <h3>{texts.presets}</h3>
            <div className={presets_grid}>
              {GRADIENT_PRESETS.map((preset, index) => (
                <button
                  key={preset.join("-")}
                  type="button"
                  className={preset_button}
                  style={{
                    background: `linear-gradient(135deg, ${preset[0]}, ${preset[1]})`,
                  }}
                  onClick={() => applyPreset(preset)}
                  aria-label={`${texts.usePreset} ${index + 1}: ${preset.join(
                    " – "
                  )}`}
                />
              ))}
            </div>
          </div>
        </aside>
      </div>

      <section className={scale_panel} aria-labelledby="gradient-colors-title">
        <h2 id="gradient-colors-title" className={panel_heading}>
          {texts.colorRange}
        </h2>
        <div className={colors_scale}>
          {colorScale.map((color, index) => {
            const normalizedColor = color.toUpperCase();
            return (
              <button
                type="button"
                key={`${normalizedColor}-${index}`}
                className={scale_color}
                style={{
                  backgroundColor: normalizedColor,
                  color: darkOrLightColor(normalizedColor),
                }}
                onClick={() => copyText(normalizedColor)}
                aria-label={`${texts.copyValue}: ${normalizedColor}`}
              >
                {normalizedColor}
              </button>
            );
          })}
        </div>
      </section>

      {toastMessage ? (
        <div className={toast} role="status">
          {toastMessage}
        </div>
      ) : null}
    </section>
  );
};

export default GradientGenerator;
